const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/eCheckin/components/fields/ArztBlock.tsx';
import * as React from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { useIntl } from 'react-intl';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from 'App/config/common/translations';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { AddressBlock, AddressFieldsConfig, defaultAddressFieldsConfig } from './AddressBlock';
import { CIS4mPhoneNumberField } from '@cistec/common_web/modules/CIS4m/components/CIS4mPhoneNumberField';
import { isValidPhoneNumber, validatePhoneNumber } from '@cistec/common_web/components/Forms/utils/validation';
import { clearGepartnerSearchResults, getGepartnerSearchResults } from 'App/actions/gepartner';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { CIS4mAutocomplete } from '@cistec/common_web/modules/CIS4m/components/CIS4mAutocomplete';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';
import { actionSetCIS4mDataObject } from '@cistec/common_web/modules/CIS4m/actions';
import { useCIS4mContext } from '@cistec/common_web/modules/CIS4m/context';
import { normalizePhoneNumber } from 'App/utils/phoneNumberUtils';
import { makeSelectGepartnerSearchResults } from 'App/store/gepartnerSearch';
import { FieldConfig, InputFieldConfig, TextFieldConfig } from '../../types';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { CIS4mHiddenField } from '@cistec/common_web/modules/CIS4m/components/CIS4mHiddenField';
import { useMadeSelector } from '@cistec/common_web/utils/store';

export interface ArztFieldsConfig {
    firstName?: TextFieldConfig;
    lastName?: TextFieldConfig;
    zusatz?: TextFieldConfig;
    tel?: TextFieldConfig;
    key?: FieldConfig;
}

export const defaultArztFieldsConfig: Required<ArztFieldsConfig> = {
    firstName: {
        path: 'firstname',
        required: true,
        included: true,
        readonly: false,
    },
    lastName: {
        path: 'lastname',
        required: true,
        included: true,
        readonly: false,
    },
    zusatz: {
        path: 'zusatz',
        required: false,
        included: false,
        readonly: false,
    },
    tel: {
        path: 'tel',
        required: true,
        included: true,
        readonly: false,
    },
    key: {
        path: 'key',
        included: true,
    },
};

interface Props {
    basePath: string;
    required?: boolean;
    hidden?: boolean;
    readonly?: boolean;
    fieldsConfig?: ArztFieldsConfig;
    addressFieldsConfig?: AddressFieldsConfig;
}

const useGepartnerSearchResults = (searchValue: string) => {
    const dispatch = useDispatch();
    const [componentId] = useState(uuidv4());

    useEffect(() => {
        return () => {
            dispatch(clearGepartnerSearchResults(componentId));
        };
    }, []);

    useEffect(() => {
        if (searchValue && searchValue.length > 2) {
            dispatch(getGepartnerSearchResults(searchValue, componentId));
        } else {
            dispatch(clearGepartnerSearchResults(componentId));
        }
    }, [searchValue]);

    return useMadeSelector(() => makeSelectGepartnerSearchResults(componentId), [componentId]);
};

const getFieldsConfig = (fieldsConfig?: ArztFieldsConfig) => {
    return _.merge({}, defaultArztFieldsConfig, fieldsConfig);
};
const getAddressFieldsConfig = (addressFieldsConfig?: AddressFieldsConfig) => {
    return _.merge({}, defaultAddressFieldsConfig, addressFieldsConfig);
};

export const ArztBlock = forwardRef((props: Props, ref: any) => {
    const { basePath } = props;

    const fieldsMap = _.merge(
        {},
        getFieldsConfig(props.fieldsConfig),
        getAddressFieldsConfig(props.addressFieldsConfig)
    );

    const { firstName, lastName, zusatz, tel, key, streetAndNumber, postalCode, location, country } = fieldsMap;

    const PATH_FIRST_NAME = createFieldPath(basePath, firstName.path!);
    const PATH_LAST_NAME = createFieldPath(basePath, lastName.path!);
    const PATH_ZUSATZ = createFieldPath(basePath, zusatz.path!);
    const PATH_COUNTRY = createFieldPath(basePath, country.path);
    const PATH_TEL = createFieldPath(basePath, tel.path!);
    const PATH_GEPARTNER_KEY = createFieldPath(basePath, key.path!);

    const PATH_STREET = createFieldPath(basePath, streetAndNumber.path);
    const PATH_PLZ = createFieldPath(basePath, postalCode.path);
    const PATH_ORT = createFieldPath(basePath, location.path);

    const { formatMessage } = useIntl();
    const dispatch = useDispatch<any>();
    const mobileFormBlockRef = useCIS4m(ref);
    const mobileFormData = useCIS4mData();
    const { formId } = useCIS4mContext();

    const required = props.required ?? false;
    const hidden = props.hidden ?? false;
    const readonly = props.readonly ?? false;

    const [searchValue, setSearchValue] = useState(_.get(mobileFormData, PATH_LAST_NAME));
    const gepartnerSearchResult = useGepartnerSearchResults(searchValue);

    const renderOption = (props: KeyValuePair, option: KeyValuePair) => {
        return (
            <li {...props} key={option.OBJECTKEY}>
                {`${option.Titel} ${option.Vorname} ${option.Name}`.trim().replace(/\s+/g, ' ')}
                <br />
                {option.Strasse}
                <br />
                {option.PLZ} {option.Ort}
            </li>
        );
    };

    const getOptionLabel = (option: any) => {
        if (typeof option === 'object') return option?.Name;
        if (typeof option === 'string') return option;
    };

    const clearFields = () => {
        const newFormData = _.clone(mobileFormData);
        const paths = [
            PATH_LAST_NAME,
            PATH_FIRST_NAME,
            PATH_ZUSATZ,
            PATH_TEL,
            PATH_STREET,
            PATH_PLZ,
            PATH_ORT,
            PATH_COUNTRY,
            PATH_GEPARTNER_KEY,
        ];
        paths.forEach(path => _.unset(newFormData, path));
        dispatch(actionSetCIS4mDataObject(formId, newFormData));
    };

    const storeSelectedOption = (option: KeyValuePair) => {
        const newFormData = _.clone(mobileFormData);

        const telValue = normalizePhoneNumber(option.Tel);

        const fields = [
            lastName.included && { path: PATH_LAST_NAME, value: option.Name },
            firstName.included && { path: PATH_FIRST_NAME, value: option.Vorname },
            zusatz.included && { path: PATH_ZUSATZ, value: option.zusatz },
            tel.included && { path: PATH_TEL, value: isValidPhoneNumber(telValue) ? telValue : '' },
            streetAndNumber.included && { path: PATH_STREET, value: option.Strasse },
            postalCode.included && { path: PATH_PLZ, value: option.PLZ },
            location.included && { path: PATH_ORT, value: option.Ort },
            country.included && { path: PATH_COUNTRY, value: option.Land },
            key.included && { path: PATH_GEPARTNER_KEY, value: option.OBJECTKEY },
        ];

        _.compact(fields).forEach((field: any) => {
            _.set(newFormData, field.path, field.value);
        });

        dispatch(actionSetCIS4mDataObject(formId, newFormData));
    };

    const handleAutoCompleteChange = (value: any) => {
        if (typeof value === 'string') {
            setSearchValue(value);

            if (readonly) {
                clearFields();
            }
        } else {
            storeSelectedOption(value);
        }
    };

    const isReadonly = (fieldConfig: InputFieldConfig) => {
        return readonly || fieldConfig.readonly;
    };
    const isRequired = (fieldConfig: InputFieldConfig) => {
        return !isReadonly(fieldConfig) && required && fieldConfig.required;
    };

    return (
        <CIS4mBlock ref={mobileFormBlockRef} hidden={hidden}>
            <CIS4mHiddenField path={PATH_GEPARTNER_KEY} />
            <CIS4mAutocomplete
                label={formatMessage(textMapCommon.lastname)}
                path={PATH_LAST_NAME}
                options={gepartnerSearchResult.list || []}
                maxLength={lastName.maxLength}
                renderOption={renderOption}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionLabel}
                onChange={handleAutoCompleteChange}
                required={true}
                freeSolo={!isReadonly(lastName)}
            />
            <CIS4mTextField
                label={textMapCommon.firstname}
                path={PATH_FIRST_NAME}
                hidden={!firstName.included}
                maxLength={firstName.maxLength}
                required={isRequired(firstName)}
                readonly={isReadonly(firstName)}
                helperText={firstName.helperText}
            />
            <CIS4mTextField
                label={textMapCommon.zusatz}
                path={PATH_ZUSATZ}
                hidden={!zusatz.included}
                required={isRequired(zusatz)}
                readonly={isReadonly(zusatz)}
                helperText={zusatz.helperText}
            />
            <AddressBlock
                readonly={readonly}
                basePath={props.basePath}
                required={required}
                fieldsConfig={props.addressFieldsConfig}
            />
            <CIS4mPhoneNumberField
                label={textMapCommon.telGeneral}
                path={PATH_TEL}
                checkValidity={validatePhoneNumber}
                countryCodePath={PATH_COUNTRY}
                hidden={!tel.included}
                required={isRequired(tel)}
                readonly={isReadonly(tel)}
            />
        </CIS4mBlock>
    );
});

ArztBlock.displayName = 'ArztBlock';
